import { useEffect } from "react";

type SchemaType = "Organization" | "SoftwareApplication" | "FAQPage" | "Service" | "Article";

interface FAQItem {
  question: string;
  answer: string;
}

interface SchemaMarkupProps {
  type: SchemaType;
  data?: {
    faqs?: FAQItem[];
    serviceName?: string;
    serviceDescription?: string;
    articleTitle?: string;
    articleAuthor?: string;
    articleDate?: string;
    articleImage?: string;
  };
}

function buildSchema(type: SchemaType, data?: SchemaMarkupProps["data"]): object | null {
  switch (type) {
    case "Organization":
      return {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "NeoSDR",
        url: "https://neosdr.online",
        logo: "https://neosdr.online/favicon.png",
        sameAs: [],
        contactPoint: {
          "@type": "ContactPoint",
          contactType: "sales",
          availableLanguage: ["Portuguese"],
        },
      };

    case "SoftwareApplication":
      return {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: "NeoSDR CRM",
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
        offers: {
          "@type": "AggregateOffer",
          priceCurrency: "BRL",
          lowPrice: "199",
          highPrice: "499",
          offerCount: "3",
        },
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.8",
          ratingCount: "500",
        },
      };

    case "FAQPage":
      if (!data?.faqs?.length) return null;
      return {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: data.faqs.map((faq) => ({
          "@type": "Question",
          name: faq.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.answer,
          },
        })),
      };

    case "Service":
      return {
        "@context": "https://schema.org",
        "@type": "Service",
        serviceType: data?.serviceName || "Pré-vendas B2B",
        provider: {
          "@type": "Organization",
          name: "NeoSDR",
        },
        description:
          data?.serviceDescription ||
          "Serviço de terceirização de pré-vendas B2B com SDRs especializados para gerar reuniões qualificadas.",
        areaServed: { "@type": "Country", name: "BR" },
      };

    case "Article":
      return {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: data?.articleTitle || "",
        author: {
          "@type": "Person",
          name: data?.articleAuthor || "NeoSDR",
        },
        publisher: {
          "@type": "Organization",
          name: "NeoSDR",
          logo: { "@type": "ImageObject", url: "https://neosdr.online/favicon.png" },
        },
        datePublished: data?.articleDate || "",
        ...(data?.articleImage ? { image: data.articleImage } : {}),
        mainEntityOfPage: { "@type": "WebPage", "@id": typeof window !== "undefined" ? window.location.href : "" },
      };

    default:
      return null;
  }
}

/**
 * Injects JSON-LD structured data into <head>.
 * Each instance creates its own <script> tag and cleans up on unmount.
 */
export function SchemaMarkup({ type, data }: SchemaMarkupProps) {
  useEffect(() => {
    const schema = buildSchema(type, data);
    if (!schema) return;

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(schema);
    script.setAttribute("data-schema-type", type);
    document.head.appendChild(script);

    return () => {
      script.remove();
    };
  }, [type, data]);

  return null;
}
